// Plays back model audio (24 kHz int16 PCM) by queueing AudioBufferSourceNodes.
//
// We use a fixed-rate AudioContext so the browser does not resample the model's
// output, avoiding aliasing artifacts. Queued buffers are scheduled in
// monotonically increasing `nextStartTime` so the output is gap-free as long
// as the network keeps up.

export class AudioPlayer {
  constructor(sampleRate) {
    this.sampleRate = sampleRate;
    this.ctx = null;
    this.nextStartTime = 0;
    this.sources = new Set();
  }

  _ensureContext() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: this.sampleRate,
      });
    }
    if (this.ctx.state === "suspended") {
      this.ctx.resume().catch(() => {});
    }
  }

  enqueue(arrayBuffer) {
    this._ensureContext();
    const i16 = new Int16Array(arrayBuffer);
    if (i16.length === 0) return;
    const f32 = new Float32Array(i16.length);
    // 32768 = 2^15: scale int16 -> [-1, 1].
    for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 0x8000;

    const buf = this.ctx.createBuffer(1, f32.length, this.sampleRate);
    buf.copyToChannel(f32, 0);

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);

    const startTime = Math.max(this.ctx.currentTime + 0.02, this.nextStartTime);
    src.start(startTime);
    this.nextStartTime = startTime + buf.duration;

    this.sources.add(src);
    src.onended = () => this.sources.delete(src);
  }

  stopAll() {
    for (const src of this.sources) {
      try {
        src.stop();
      } catch (_) {}
    }
    this.sources.clear();
    if (this.ctx) this.nextStartTime = this.ctx.currentTime;
  }
}
